
import { supabase } from "@/integrations/supabase/client";

interface AuthResponse {
  success: boolean;
  view?: string;
  error?: string;
  isNewUser?: boolean;
}

export const handleSignUp = async (
  email: string,
  password: string,
  userType: string
): Promise<AuthResponse> => {
  const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        user_type: userType
      }
    }
  });

  if (signUpError) return { success: false, error: signUpError.message };

  if (signUpData?.user) {
    // Map the new account types to appropriate roles
    const getRoleFromAccountType = (accountType: string): "financial_analyst" | "compliance_manager" | "e_concierge" | "admin" => {
      switch (accountType) {
        case 'quant':
          return 'financial_analyst';
        case 'quant-pro':
          return 'financial_analyst'; // Could be enhanced to have a 'senior_analyst' role
        case 'enterprise':
          return 'compliance_manager'; // Higher-tier gets compliance access
        default:
          return 'financial_analyst';
      }
    };

    const { error: roleError } = await supabase
      .from('user_roles')
      .insert([
        { 
          user_id: signUpData.user.id, 
          role: getRoleFromAccountType(userType)
        }
      ]);

    if (roleError) return { success: false, error: roleError.message };

    return { success: true, isNewUser: true };
  }

  return { success: false, error: "Signup failed" };
};

export const handleSignIn = async (
  email: string,
  password: string
): Promise<AuthResponse> => {
  const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (signInError) return { success: false, error: signInError.message };

  if (signInData.user) {
    try {
      const { data: roles, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', signInData.user.id);

      if (roleError) throw roleError;

      const role = roles && roles.length > 0 ? roles[0].role : null;
      const initialView = role === 'compliance_manager' ? 'chat' : 'trader';
      
      return { success: true, view: initialView, isNewUser: false };
    } catch (roleError) {
      console.error('Error fetching role:', roleError);
      return { success: true, view: 'trader', isNewUser: false };
    }
  }

  return { success: false, error: "Sign in failed" };
};

export const handleGoogleAuth = async (): Promise<AuthResponse> => {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
    },
  });

  if (error) return { success: false, error: error.message };
  return { success: true };
};
